// Account-analytics count SNAPSHOT cores — PURE, framework-free, tested (Phase C).
//
// Three concerns, all pure (no Firestore / no clock reads), shared by the server
// snapshot service + reconstruction script + the client count-history read:
//   1. buildSnapshotCounts — a roster → active-operational counts by dimension id.
//   2. Reconstruction — per-account audit-diff timelines → the as-of state at any
//      month-end → month-end counts (CURRENT type/brand/industry/group is the
//      documented approximation; AM/status/existence come from the audit history).
//   3. buildCountHistory — stored snapshot points → the ordered read payload with
//      brand/group/AM id→label resolution (departed AM → fallback).
//
// Definition of a counted account (matches the AM-lens default): status 'active'
// AND accountType ∈ OPERATIONAL_ACCOUNT_TYPES. Money-free (these are counts).

import type { AccountStatus, AccountType, Industry } from '../types/account.js';
import type {
  AccountCountHistory,
  AccountCountSnapshotPoint,
} from '../types/accountAnalytics.js';
import { OPERATIONAL_ACCOUNT_TYPES } from './accountAnalytics.js';

// Count-map bucket sentinels for a null brand / group / AM — these are STORED as
// Firestore MAP KEYS (field names), so they must avoid BOTH a blank key AND the
// reserved-name pattern /^__.*__$/ (leading + trailing double underscore, same
// class as the empty-key bug). Single leading underscore, no trailing "__". NB:
// the AM bucket does NOT reuse the client filter's UNASSIGNED_AM ('__unassigned__')
// — that value is only ever an array element / URL param (never a field name), so
// it stays reserved-looking there; here we need a Firestore-safe key.
export const NO_BRAND_KEY = '_nobrand';
export const NO_GROUP_KEY = '_nogroup';
export const NO_AM_KEY = '_unassigned';

// Fallback label for an AM uid no longer in the directory (departed staff) — the
// read still surfaces their historical accounts under a stable, non-uid label.
export const DEPARTED_AM_LABEL = 'Former team member';

function isOperational(type: AccountType): boolean {
  return OPERATIONAL_ACCOUNT_TYPES.includes(type);
}

// The minimal per-account shape the count builder needs: the as-of AM + status
// (historical when reconstructing, live when snapshotting) + the CURRENT tree
// dimensions (brand/group ancestor ids, industry, type).
export interface SnapshotAccount {
  accountType: AccountType;
  status: AccountStatus;
  brandAccountId: string | null; // nearest brand-type ancestor id
  groupAccountId: string | null; // nearest group-type ancestor id
  industry: Industry;
  accountManagerId: string | null;
}

export interface SnapshotCounts {
  totalActiveOperational: number;
  byBrand: Record<string, number>;
  byIndustry: Record<string, number>;
  byGroup: Record<string, number>;
  byAm: Record<string, number>;
}

function bump(map: Record<string, number>, key: string): void {
  map[key] = (map[key] ?? 0) + 1;
}

// Count the active + operational accounts by brand / industry / group / AM (each
// keyed by ID, null → the bucket sentinel) + the grand total. Non-active or
// non-operational accounts are excluded entirely.
export function buildSnapshotCounts(accounts: SnapshotAccount[]): SnapshotCounts {
  const counts: SnapshotCounts = {
    totalActiveOperational: 0,
    byBrand: {},
    byIndustry: {},
    byGroup: {},
    byAm: {},
  };
  for (const a of accounts) {
    if (a.status !== 'active' || !isOperational(a.accountType)) continue;
    counts.totalActiveOperational += 1;
    bump(counts.byBrand, a.brandAccountId ?? NO_BRAND_KEY);
    bump(counts.byIndustry, a.industry);
    bump(counts.byGroup, a.groupAccountId ?? NO_GROUP_KEY);
    bump(counts.byAm, a.accountManagerId ?? NO_AM_KEY);
  }
  return counts;
}

// ---- month helpers (UTC, no clock read) ------------------------------------

const pad2 = (n: number): string => String(n).padStart(2, '0');

// 'YYYY-MM' for a 1-based month.
export function monthKey(year: number, month1: number): string {
  return `${year}-${pad2(month1)}`;
}

// The 'YYYY-MM' of the calendar month BEFORE `now`'s month (UTC). The monthly job
// captures the PRIOR completed month.
export function priorCompletedMonth(now: Date): string {
  const y = now.getUTCFullYear();
  const m0 = now.getUTCMonth(); // 0-based current month
  const prev = new Date(Date.UTC(y, m0 - 1, 1));
  return monthKey(prev.getUTCFullYear(), prev.getUTCMonth() + 1);
}

// The last calendar day of a 'YYYY-MM' as 'YYYY-MM-DD' (UTC) — the snapshot's
// stored monthEnd marker.
export function monthEndIso(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const end = new Date(Date.UTC(y!, m!, 0)); // day 0 of next month = last day of this
  return `${end.getUTCFullYear()}-${pad2(end.getUTCMonth() + 1)}-${pad2(end.getUTCDate())}`;
}

// First instant (ms) of the month AFTER `month` — the EXCLUSIVE upper bound for
// "state as of the end of `month`".
export function monthEndExclusiveMs(month: string): number {
  const [y, m] = month.split('-').map(Number);
  return Date.UTC(y!, m!, 1); // m is 1-based → Date.UTC month index m = next month
}

// The ascending list of 'YYYY-MM' from `first` to `last` inclusive.
export function monthsBetween(first: string, last: string): string[] {
  const out: string[] = [];
  const [fy, fm] = first.split('-').map(Number);
  let y = fy!;
  let m = fm!; // 1-based
  while (monthKey(y, m) <= last) {
    out.push(monthKey(y, m));
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

// ---- reconstruction from audit diffs ---------------------------------------

// One account audit entry, normalised to the fields reconstruction needs: the
// action, the entry's own timestamp (ms), and the changed fields (create carries
// EVERY field as newValue, update only the changed ones — the diff shape).
export interface ReconAuditEntry {
  entityId: string;
  action: 'create' | 'update' | 'delete';
  timestampMs: number;
  changes: Array<{ field: string; newValue: unknown }>;
}

// A per-account replayable timeline: existence window + the ordered AM/status
// values (each seeded by the create entry, then appended by updates that changed
// that field). No CURRENT-state assumptions — this is purely the audit history.
export interface AccountTimeline {
  createdMs: number | null; // null → never saw a create entry (can't place existence)
  deletedMs: number | null;
  amAt: Array<{ ms: number; value: string | null }>;
  statusAt: Array<{ ms: number; value: string }>;
}

function changeValue(entry: ReconAuditEntry, field: string): { present: boolean; value: unknown } {
  const c = entry.changes.find((ch) => ch.field === field);
  return c ? { present: true, value: c.newValue } : { present: false, value: undefined };
}

// Build each account's timeline from its (unordered) audit entries. Entries are
// sorted by timestamp so create seeds first and updates replay in order.
export function buildAccountTimelines(
  entries: ReconAuditEntry[],
): Map<string, AccountTimeline> {
  const byId = new Map<string, ReconAuditEntry[]>();
  for (const e of entries) {
    const list = byId.get(e.entityId);
    if (list) list.push(e);
    else byId.set(e.entityId, [e]);
  }

  const timelines = new Map<string, AccountTimeline>();
  for (const [id, list] of byId) {
    const sorted = [...list].sort((a, b) => a.timestampMs - b.timestampMs);
    const tl: AccountTimeline = { createdMs: null, deletedMs: null, amAt: [], statusAt: [] };
    for (const e of sorted) {
      if (e.action === 'create') tl.createdMs = e.timestampMs;
      if (e.action === 'delete') tl.deletedMs = e.timestampMs;
      const am = changeValue(e, 'accountManagerId');
      if (am.present) tl.amAt.push({ ms: e.timestampMs, value: (am.value as string | null) ?? null });
      const st = changeValue(e, 'status');
      if (st.present && typeof st.value === 'string') tl.statusAt.push({ ms: e.timestampMs, value: st.value });
    }
    timelines.set(id, tl);
  }
  return timelines;
}

// The value of a monotonic change-list as of `asOfMs` (the last change strictly
// before it), or null if none precedes it.
function valueAsOf<T>(changes: Array<{ ms: number; value: T }>, asOfMs: number): T | null {
  let value: T | null = null;
  for (const c of changes) {
    if (c.ms < asOfMs) value = c.value;
    else break; // changes are appended in ascending ms order
  }
  return value;
}

// The account's AM + status as of `asOfMs`, or null if it did NOT exist then
// (created on/after the bound, or deleted strictly before it).
export function accountStateAsOf(
  tl: AccountTimeline,
  asOfMs: number,
): { accountManagerId: string | null; status: string } | null {
  if (tl.createdMs === null || tl.createdMs >= asOfMs) return null;
  if (tl.deletedMs !== null && tl.deletedMs < asOfMs) return null;
  return {
    accountManagerId: valueAsOf(tl.amAt, asOfMs),
    status: valueAsOf(tl.statusAt, asOfMs) ?? 'active',
  };
}

// The CURRENT tree dimensions joined onto a reconstructed account (existence +
// AM/status come from the timeline; these are today's values — the approximation).
export interface CurrentAccountDimension {
  accountType: AccountType;
  brandAccountId: string | null;
  groupAccountId: string | null;
  industry: Industry;
}

// Reconstruct the SnapshotAccount set as of `month`'s end: every account that
// existed then (per its timeline) AND is still in the current tree (so its
// type/brand/group/industry resolve), carrying its as-of AM + status.
export function reconstructSnapshotAccounts(
  timelines: Map<string, AccountTimeline>,
  currentById: Map<string, CurrentAccountDimension>,
  month: string,
): SnapshotAccount[] {
  const asOfMs = monthEndExclusiveMs(month);
  const out: SnapshotAccount[] = [];
  for (const [id, tl] of timelines) {
    const st = accountStateAsOf(tl, asOfMs);
    if (!st) continue;
    const cur = currentById.get(id);
    if (!cur) continue; // no current dimensions → can't classify (documented drop)
    out.push({
      accountType: cur.accountType,
      brandAccountId: cur.brandAccountId,
      groupAccountId: cur.groupAccountId,
      industry: cur.industry,
      status: st.status as AccountStatus,
      accountManagerId: st.accountManagerId,
    });
  }
  return out;
}

// The month-end active-operational counts for a reconstructed month (compose of
// the two cores above).
export function reconstructMonthCounts(
  timelines: Map<string, AccountTimeline>,
  currentById: Map<string, CurrentAccountDimension>,
  month: string,
): SnapshotCounts {
  return buildSnapshotCounts(reconstructSnapshotAccounts(timelines, currentById, month));
}

// The earliest 'YYYY-MM' any account was created in (per the timelines) — the
// first month reconstruction can support. null when there are no create entries.
export function earliestTimelineMonth(timelines: Map<string, AccountTimeline>): string | null {
  let min: number | null = null;
  for (const tl of timelines.values()) {
    if (tl.createdMs !== null && (min === null || tl.createdMs < min)) min = tl.createdMs;
  }
  if (min === null) return null;
  const d = new Date(min);
  return monthKey(d.getUTCFullYear(), d.getUTCMonth() + 1);
}

// ---- count-history read (label resolution) ---------------------------------

// Assemble the read payload from stored snapshot points: sort ascending by month
// and build the id→name maps (brand/group via the account-name map; AM via the
// directory, a departed uid → the fallback label). The count sentinels are left
// out of the name maps — the pivot resolves those to "(no brand)" etc.
export function buildCountHistory(
  points: AccountCountSnapshotPoint[],
  nameByAccountId: Map<string, string>,
  amNameByUid: Map<string, string | null>,
): AccountCountHistory {
  const ordered = [...points].sort((a, b) => a.month.localeCompare(b.month));

  const brandNames: Record<string, string> = {};
  const groupNames: Record<string, string> = {};
  const amNames: Record<string, string> = {};
  for (const p of ordered) {
    for (const id of Object.keys(p.byBrand)) {
      if (id !== NO_BRAND_KEY && !(id in brandNames)) brandNames[id] = nameByAccountId.get(id) ?? id;
    }
    for (const id of Object.keys(p.byGroup)) {
      if (id !== NO_GROUP_KEY && !(id in groupNames)) groupNames[id] = nameByAccountId.get(id) ?? id;
    }
    for (const uid of Object.keys(p.byAm)) {
      if (uid !== NO_AM_KEY && !(uid in amNames)) {
        amNames[uid] = amNameByUid.get(uid) ?? DEPARTED_AM_LABEL;
      }
    }
  }
  return { points: ordered, brandNames, groupNames, amNames };
}
